import { Registry, type Identified } from './Registry';
import type { Rarity } from './materials';

/**
 * Balık türleri ve yakalanma koşulları.
 * Olasılık hesabı tamamen veriden gelir: yeni balık eklemek için
 * buraya bir kayıt eklemek yeterli, Fishing sistemi değişmez.
 */

export type WaterKind = 'sea' | 'river' | 'lake' | 'swamp' | 'ice' | 'venom';
export type TimeWindow = 'any' | 'day' | 'night';
export type WeatherKind = 'clear' | 'rain' | 'fog' | 'snow';

export interface FishDef extends Identified {
  name: string;
  icon: string;
  rarity: Rarity;
  /** Temel ağırlık; nadirlikten bağımsız ince ayar. */
  weight: number;
  waters: WaterKind[];
  time: TimeWindow;
  /** Bu havalarda ağırlığı artar. */
  weatherBonus?: Partial<Record<WeatherKind, number>>;
  /** Satış değeri (Kogox). */
  value: number;
  hunger: number;
  /** Yakalamak için gereken minimum olta seviyesi. */
  rodLevel: number;
  desc?: string;
}

export const fishSpecies = new Registry<FishDef>('fish');

fishSpecies.register(
  { id: 'fish_minnow', name: 'Küçük Balık', icon: '🐟', rarity: 'common', weight: 100, waters: ['river', 'lake', 'sea'], time: 'any', value: 5, hunger: 8, rodLevel: 1 },
  { id: 'fish_carp', name: 'Sazan', icon: '🐠', rarity: 'common', weight: 80, waters: ['river', 'lake'], time: 'any', value: 9, hunger: 12, rodLevel: 1 },
  { id: 'fish_bass', name: 'Levrek', icon: '🐟', rarity: 'common', weight: 70, waters: ['sea', 'river'], time: 'day', value: 12, hunger: 14, rodLevel: 1 },
  { id: 'fish_trout', name: 'Alabalık', icon: '🐟', rarity: 'uncommon', weight: 45, waters: ['river'], time: 'any', weatherBonus: { rain: 2 }, value: 22, hunger: 16, rodLevel: 1 },
  { id: 'fish_eel', name: 'Yılan Balığı', icon: '🐍', rarity: 'uncommon', weight: 35, waters: ['swamp', 'river'], time: 'night', value: 34, hunger: 14, rodLevel: 1 },
  { id: 'fish_catfish', name: 'Yayın', icon: '🐡', rarity: 'uncommon', weight: 30, waters: ['river', 'lake'], time: 'night', weatherBonus: { rain: 1.6 }, value: 40, hunger: 20, rodLevel: 1 },
  { id: 'fish_ice_cod', name: 'Buz Morinası', icon: '🐟', rarity: 'rare', weight: 18, waters: ['ice'], time: 'any', weatherBonus: { snow: 2.2 }, value: 85, hunger: 22, rodLevel: 2 },
  { id: 'fish_swamp_lurker', name: 'Bataklık Sinsisi', icon: '🐸', rarity: 'rare', weight: 15, waters: ['swamp'], time: 'night', weatherBonus: { fog: 2 }, value: 110, hunger: 18, rodLevel: 2 },
  { id: 'fish_silverfin', name: 'Gümüş Yüzgeç', icon: '🐠', rarity: 'rare', weight: 14, waters: ['sea', 'river'], time: 'night', value: 130, hunger: 20, rodLevel: 2 },
  { id: 'fish_venom_ray', name: 'Zehir Vatozu', icon: '🟣', rarity: 'veryRare', weight: 5, waters: ['venom'], time: 'any', value: 320, hunger: 10, rodLevel: 2, desc: 'Mor biyoma özgü.' },
  { id: 'fish_moon_koi', name: 'Ay Koisi', icon: '🌙', rarity: 'veryRare', weight: 4, waters: ['lake', 'river'], time: 'night', weatherBonus: { rain: 2.5 }, value: 400, hunger: 24, rodLevel: 2 },
  { id: 'fish_storm_marlin', name: 'Fırtına Kılıcı', icon: '⚡', rarity: 'veryRare', weight: 3.5, waters: ['sea'], time: 'any', weatherBonus: { rain: 3 }, value: 480, hunger: 26, rodLevel: 2 },
  { id: 'fish_kogox_leviathan', name: 'Kogox Leviathanı', icon: '🐉', rarity: 'legendary', weight: 1, waters: ['sea', 'river'], time: 'night', weatherBonus: { rain: 3.5, fog: 2 }, value: 1000, hunger: 40, rodLevel: 2, desc: 'Efsanevi. BEDO bunu görünce şaşırır.' },
);

/**
 * Gece ve yağmur nadir balık şansını ciddi biçimde artırır.
 * Hedef: tek koşulda ~5 kata kadar; iki koşul üst üste binerse çarpım
 * sınırlandırılır ki oyun bozulmasın.
 */
export const RARE_BONUS = Object.freeze({
  night: 5,
  rain: 5,
  /** İki bonusun çarpımı bu değeri aşamaz. */
  maxCombined: 7,
});

/** Nadirlik -> bonusun uygulanma oranı (yaygın balığa bonus yok). */
export const RARE_BONUS_APPLIES: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 0,
  uncommon: 0.35,
  rare: 1,
  veryRare: 1,
  legendary: 1,
});
